// Polish #1 — "Compose new ticket from scratch" mutation + draft helper.
//
// Two hooks live here because they're used exclusively together by the
// NewTicketModal, and co-locating keeps the type surface obvious:
//
//   useComposeNewTicket()   — POST /api/conversations/new
//                             creates the Gmail thread + convo row
//   useDraftFromPrompt()    — POST /api/ai/compose
//                             one-line prompt → Haiku-written body seed
//
// On successful send we invalidate the 'conversations' query so the new
// thread shows up in Standup (as awaiting/outbound it'll eventually land
// in Circle Back — the bucket logic hides outbound under 2 days old so
// the user won't see it until it's actually stale). The caller decides
// what to do next (usually: navigate to /thread/:id).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';

export type ComposeBrand = 'md' | 'hk';

export interface ComposeNewTicketPayload {
  brand: ComposeBrand;
  to: string;
  subject: string;
  body: string;
}

export interface ComposeNewTicketResponse {
  ok: true;
  conversation_id: string;
  message_id: string | null;
  external_message_id: string | null;
  external_thread_id: string | null;
}

export function useComposeNewTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ComposeNewTicketPayload): Promise<ComposeNewTicketResponse> => {
      return apiPost<ComposeNewTicketResponse, ComposeNewTicketPayload>(
        '/api/conversations/new',
        payload,
      );
    },
    onSuccess: () => {
      // Refresh Standup buckets so the new awaiting/outbound row is present
      // if/when it later qualifies for Circle Back.
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export interface DraftFromPromptPayload {
  brand: ComposeBrand;
  prompt: string;
  to_name?: string;
}

export interface DraftFromPromptResponse {
  ok: true;
  text: string;
  usage: { input_tokens: number; output_tokens: number };
  model: string;
}

export function useDraftFromPrompt() {
  return useMutation({
    mutationFn: async (payload: DraftFromPromptPayload): Promise<DraftFromPromptResponse> => {
      return apiPost<DraftFromPromptResponse, DraftFromPromptPayload>(
        '/api/ai/compose',
        payload,
      );
    },
  });
}
